// Small pieces shared by Online Payments and Short Pay (db/migrations/8002,
// 8003). Both follow the request, approve, pay shape of Petrol Requests; the
// rules live in Postgres, and these only lay out what it says.
import type { ReactNode } from 'react'
import { ShieldAlert } from 'lucide-react'
import { Button } from '../../lib/shadcn/button'
import { Spinner } from '../../lib/shadcn/spinner'

export type RequestStatus = 'requested' | 'approved' | 'paid' | 'declined' | 'cancelled'

export const REQUEST_TONE: Record<RequestStatus, 'green' | 'red' | 'amber' | 'neutral' | 'brand'> = {
  requested: 'amber',
  approved: 'brand',
  paid: 'green',
  declined: 'red',
  cancelled: 'neutral',
}

export const STATUS_FILTERS = (approvedLabel: string): { value: string; label: string }[] => [
  { value: 'open', label: 'Open' },
  { value: '', label: 'All' },
  { value: 'requested', label: 'Waiting for approval' },
  { value: 'approved', label: approvedLabel },
  { value: 'paid', label: 'Paid' },
  { value: 'declined', label: 'Declined' },
  { value: 'cancelled', label: 'Cancelled' },
]

export function Row({ k, v }: { k: string; v: ReactNode }) {
  return (
    <div className="flex gap-2">
      <dt className="w-36 shrink-0 text-muted-foreground">{k}</dt>
      <dd className="min-w-0 break-words text-foreground">{v}</dd>
    </div>
  )
}

export function ErrorNote({ children }: { children: ReactNode }) {
  return <p className="rounded-md bg-rose-50 px-3 py-2 text-xs text-rose-800 dark:bg-rose-950 dark:text-rose-200">{children}</p>
}

/** Loading, a failed load with Retry, or the page's own "who this is for" note when Postgres answered null. */
export function PageGate({ loading, error, denied, onRetry, deniedTitle, deniedHint, children }: {
  loading: boolean; error: string | null | undefined; denied: boolean; onRetry: () => void
  deniedTitle: string; deniedHint: string; children: ReactNode
}) {
  if (loading) {
    return <div className="flex h-full items-center justify-center gap-2 text-sm text-muted-foreground"><Spinner className="h-4 w-4" /> Loading…</div>
  }
  if (error) {
    return (
      <div className="flex h-full items-center justify-center p-6">
        <div className="max-w-md space-y-3 rounded-lg border border-border bg-card p-6 text-center text-sm">
          <p className="text-rose-700 dark:text-rose-300">{error}</p>
          <Button size="sm" variant="outline" onClick={onRetry}>Try again</Button>
        </div>
      </div>
    )
  }
  if (denied) {
    return (
      <div className="flex h-full items-center justify-center p-6">
        <div className="flex max-w-md flex-col items-center gap-3 rounded-lg border border-border bg-card p-8 text-center">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-[hsl(var(--brand-soft))] text-[hsl(var(--brand-soft-foreground))]">
            <ShieldAlert className="h-6 w-6" />
          </div>
          <p className="text-sm font-medium text-foreground">{deniedTitle}</p>
          <p className="text-xs text-muted-foreground">{deniedHint}</p>
        </div>
      </div>
    )
  }
  return <>{children}</>
}

export const BANKS_FALLBACK = ['ABSA', 'Capitec', 'FNB', 'Nedbank', 'Standard Bank', 'TymeBank', 'African Bank', 'Discovery Bank', 'Investec', 'Other']
